// Year-specific brand tokens for the dancer-facing dashboard.
// Colors AND fonts change by festival year. Components use CSS vars only.

export const IABF_BRAND_YEAR = 2027

/**
 * Full token sets per festival year.
 * Components should use CSS vars (var(--iabf-primary), var(--iabf-font-display)), not hardcode.
 */
export const IABF_BRAND_BY_YEAR = {
  2026: {
    year: 2026,
    theme: 'light',
    // App bar mark — year logo (null = text fallback "IABF {year}")
    logoUrl: 'https://images.squarespace-cdn.com/content/v1/60fea7f9865c85083f171006/3fadb3f0-54f0-49d3-ab5d-6629d71a5e1e/IABF+2026+Logo+Transparent.png',
    logoAlt: 'IABF 2026',
    // Type
    fontDisplay: 'Quattrocento, Georgia, serif',
    fontBody: "'Fanwood Text', Georgia, serif",
    // Optional Google Fonts families to load (comma-free query pieces)
    googleFonts: [
      'Quattrocento:wght@400;700',
      'Fanwood+Text:ital@0;1'
    ],
    // Colors
    primary: '#388b9d',
    primaryPressed: '#2d6f7d',
    secondary: '#267E7D',
    secondarySoft: '#def0ef',
    accent: '#dd4762',
    bg: '#fef9f5',
    bgElevated: '#ffffff',
    bgSoft: '#fef5f0',
    bgAccentSoft: '#fff5f7',
    ink: '#1a1a1a',
    inkMuted: '#555',
    inkFaint: '#888',
    border: '#ece5d2',
    danger: '#dd4762',
    warning: '#f8722a',
    success: '#2e7d32',
    focusRing: 'rgba(56, 139, 157, 0.4)',
    // Elevated card shadow (matches style-guide)
    shadow: '0 1px 2px rgba(28, 27, 31, 0.04), 0 8px 24px rgba(28, 27, 31, 0.06)'
  },
  2027: {
    year: 2027,
    theme: 'light',
    // App bar mark — full logo art in public/brand/
    logoUrl: '/brand/iabf-2027-logo.jpg',
    logoAlt: 'IABF 2027',
    // Type — display via Adobe Fonts (kit CSS); body self-hosted OTF
    // Adobe family name is "the-seasons" (hyphenated), not "The Seasons"
    fontDisplay: 'the-seasons, Georgia, "Times New Roman", serif',
    fontBody: "'Glacial Indifference', 'Helvetica Neue', Helvetica, Arial, sans-serif",
    // Adobe Fonts web project stylesheet (paste kit ID when you have it):
    // e.g. https://use.typekit.net/xxxxxxx.css
    adobeFontsCssUrl: null,
    googleFonts: [],
    // Brand colors — sage is logo primary; lavender is secondary accent
    primary: '#8a957d',
    primaryPressed: '#6f7a64',
    secondary: '#8e82a6',
    secondarySoft: '#e8e5f0',
    accent: '#8e82a6',
    // Surfaces
    bg: '#f7f6f4',
    bgElevated: '#ffffff',
    bgSoft: '#eef0eb',
    bgAccentSoft: '#eef0eb',
    // Text
    ink: '#1c1b1f',
    inkMuted: '#5c5a63',
    inkFaint: '#8a8792',
    border: '#e3e0e8',
    // State
    danger: '#b54a4a',
    warning: '#c4923a',
    success: '#5f7a55',
    focusRing: 'rgba(138, 149, 125, 0.45)',
    // Elevated card shadow (matches style-guide)
    shadow: '0 1px 2px rgba(28, 27, 31, 0.04), 0 8px 24px rgba(28, 27, 31, 0.06)'
  }
}

export function getIabfBrand (year = IABF_BRAND_YEAR) {
  return IABF_BRAND_BY_YEAR[year] || IABF_BRAND_BY_YEAR[IABF_BRAND_YEAR]
}

/**
 * Inject font stylesheets for the brand (idempotent):
 * - Google Fonts list (if any)
 * - Adobe Fonts / Typekit CSS URL (if adobeFontsCssUrl is set)
 */
export function ensureIabfBrandFonts (year = IABF_BRAND_YEAR) {
  if (typeof document === 'undefined') {
    return
  }
  const brand = getIabfBrand(year)

  const families = brand.googleFonts || []
  if (families.length) {
    const id = `iabf-brand-google-fonts-${brand.year}`
    if (!document.getElementById(id)) {
      const href = 'https://fonts.googleapis.com/css2?family=' +
        families.join('&family=') +
        '&display=swap'
      const link = document.createElement('link')
      link.id = id
      link.rel = 'stylesheet'
      link.href = href
      document.head.appendChild(link)
    }
  }

  const adobeUrl = brand.adobeFontsCssUrl
  if (adobeUrl) {
    const id = `iabf-brand-adobe-fonts-${brand.year}`
    if (!document.getElementById(id)) {
      const link = document.createElement('link')
      link.id = id
      link.rel = 'stylesheet'
      link.href = adobeUrl
      document.head.appendChild(link)
    }
  }
}

/**
 * Apply brand tokens as CSS custom properties on an element.
 * Also loads year Google Fonts. Self-hosted faces live in iabf-fonts.css.
 */
function iconTypeForUrl (href) {
  if (/\.svg(\?|$)/i.test(href)) {
    return 'image/svg+xml'
  }
  if (/\.jpe?g(\?|$)/i.test(href)) {
    return 'image/jpeg'
  }
  if (/\.ico(\?|$)/i.test(href)) {
    return 'image/x-icon'
  }
  return 'image/png'
}

/** Tab title + favicon from this year's brand (idempotent). */
export function applyIabfDocumentChrome (year = IABF_BRAND_YEAR) {
  if (typeof document === 'undefined') {
    return
  }
  const brand = getIabfBrand(year)
  document.title = 'IABF ' + brand.year + ' Dashboard'
  const href = brand.logoUrl
  if (!href) {
    return
  }
  const type = iconTypeForUrl(href)
  let icon = document.querySelector('link[rel="icon"]')
  if (!icon) {
    icon = document.createElement('link')
    icon.setAttribute('rel', 'icon')
    document.head.appendChild(icon)
  }
  icon.setAttribute('type', type)
  icon.setAttribute('href', href)
  let apple = document.querySelector('link[rel="apple-touch-icon"]')
  if (!apple) {
    apple = document.createElement('link')
    apple.setAttribute('rel', 'apple-touch-icon')
    document.head.appendChild(apple)
  }
  apple.setAttribute('href', href)
}

export function applyIabfBrand (year = IABF_BRAND_YEAR, el) {
  const brand = getIabfBrand(year)
  ensureIabfBrandFonts(year)
  applyIabfDocumentChrome(year)
  const target = el || (typeof document !== 'undefined' ? document.documentElement : null)
  if (!target || !target.style) {
    return brand
  }
  const map = {
    '--iabf-font-display': brand.fontDisplay,
    '--iabf-font-body': brand.fontBody,
    '--iabf-primary': brand.primary,
    '--iabf-primary-pressed': brand.primaryPressed,
    '--iabf-secondary': brand.secondary,
    '--iabf-secondary-soft': brand.secondarySoft,
    '--iabf-accent': brand.accent,
    '--iabf-bg': brand.bg,
    '--iabf-bg-elevated': brand.bgElevated,
    '--iabf-bg-soft': brand.bgSoft,
    '--iabf-bg-accent-soft': brand.bgAccentSoft,
    '--iabf-ink': brand.ink,
    '--iabf-ink-muted': brand.inkMuted,
    '--iabf-ink-faint': brand.inkFaint,
    '--iabf-border': brand.border,
    '--iabf-danger': brand.danger,
    '--iabf-warning': brand.warning,
    '--iabf-success': brand.success,
    '--iabf-focus-ring': brand.focusRing,
    '--iabf-shadow': brand.shadow || '0 1px 2px rgba(28, 27, 31, 0.04), 0 8px 24px rgba(28, 27, 31, 0.06)'
  }
  Object.keys(map).forEach(key => {
    target.style.setProperty(key, map[key])
  })
  target.setAttribute('data-iabf-brand-year', String(brand.year))
  target.setAttribute('data-iabf-theme', brand.theme || 'light')
  return brand
}
